import { Except } from '@common/types/common.type';
import { clsxm } from '@ui/utils/style.util';
import { ComponentPropsWithRef, memo, useMemo } from 'react';
import Icon from '../icon/icon.atom';

// #region Types
export enum ButtonVariantEnum {
  Important = 'important',
  Info = 'info',
  Primary = 'primary',
  Regular = 'regular',
  Text = 'text',
}

export type ButtonVariant = `${ButtonVariantEnum}`;

export type ButtonProps = ComponentPropsWithRef<'button'> & {
  isLoading?: boolean;
  /**
   * Optional variant prop - defaults to 'regular' in the base Button component
   * @default 'regular'
   */
  variant?: ButtonVariant;
};
// #endregion

// #region Styles
const BUTTON_VARIANT_STYLES: Record<ButtonVariantEnum, string> = {
  [ButtonVariantEnum.Important]: 'bg-red-deep text-white-green',
  [ButtonVariantEnum.Info]: 'bg-blue-light text-white',
  [ButtonVariantEnum.Primary]: 'bg-green text-white',
  [ButtonVariantEnum.Regular]: 'bg-white-green text-green-dark',
  [ButtonVariantEnum.Text]: 'bg-transparent text-green',
};
// #endregion

// #region Components
/**
 * Base button component with three variants: regular (default), important, and text.
 * Follows a consistent design with hover and active states.
 */
function BaseButton({
  children,
  className,
  isLoading,
  onClick,
  variant = ButtonVariantEnum.Regular,
  ...rest
}: ButtonProps) {
  const variantStyle = useMemo(
    () =>
      variant === 'text'
        ? ['disabled:text-grey', BUTTON_VARIANT_STYLES.text]
        : [
            'px-4 inline-flex items-center justify-center space-x-2 rounded-full select-none shadow-button disabled:bg-grey disabled:shadow-sm',
            BUTTON_VARIANT_STYLES[variant],
          ],
    [variant],
  );

  return (
    <button
      className={clsxm(
        'h-8 font-medium cursor-pointer duration-200 hover:opacity-75 active:opacity-75 disabled:opacity-100 disabled:cursor-not-allowed',
        variantStyle,
        className,
      )}
      onClick={isLoading ? undefined : onClick}
      type="button"
      {...rest}
    >
      {isLoading && <Icon.Loading className="w-4 h-4 animate-spin mr-2" />}
      {children}
    </button>
  );
}
BaseButton.displayName = 'Button';

/**
 * Text variant of the button - uses transparent background
 */
function BaseText({ className, ...props }: Except<ButtonProps, 'variant'>) {
  return <BaseButton className={className} variant="text" {...props} />;
}
BaseText.displayName = 'Button.Text';

/**
 * Important variant of the button - uses red background for emphasis
 */
function BaseImportant({ className, ...props }: Except<ButtonProps, 'variant'>) {
  return <BaseButton className={className} variant="important" {...props} />;
}
BaseImportant.displayName = 'Button.Important';

/**
 * Primary variant of the button - uses green background for emphasis
 */
function BasePrimary({ className, ...props }: Except<ButtonProps, 'variant'>) {
  return <BaseButton className={className} variant="primary" {...props} />;
}
BasePrimary.displayName = 'Button.Primary';

/**
 * Info variant of the button - uses blue background for emphasis
 */
function BaseInfo({ className, ...props }: Except<ButtonProps, 'variant'>) {
  return <BaseButton className={className} variant="info" {...props} />;
}
BaseInfo.displayName = 'Button.Info';
// #endregion

const Button = Object.assign(memo(BaseButton), {
  Important: memo(BaseImportant),
  Info: memo(BaseInfo),
  Primary: memo(BasePrimary),
  Text: memo(BaseText),
});

export default Button;
